import { useState, useEffect, useCallback, useMemo } from 'react';
import { Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages } from '../data/mock';

export const GallerySection = () => {
  const categories = ['All', ...new Set(galleryImages.map((img) => img.category))];
  const [activeCategory, setActiveCategory] = useState('All');
  const [failedImages, setFailedImages] = useState(new Set());
  const [selectedImageId, setSelectedImageId] = useState(null);

  const filteredImages =
    activeCategory === 'All'
      ? galleryImages
      : galleryImages.filter((img) => img.category === activeCategory);

  const lightboxImages = useMemo(
    () => filteredImages.filter((img) => !failedImages.has(img.id)),
    [filteredImages, failedImages]
  );

  const selectedIndex = useMemo(
    () => lightboxImages.findIndex((img) => img.id === selectedImageId),
    [lightboxImages, selectedImageId]
  );

  const handleImageError = (id) => {
    setFailedImages((prev) => new Set([...prev, id]));
  };

  const openLightbox = (id) => setSelectedImageId(id);
  const closeLightbox = () => setSelectedImageId(null);

  const goToPrev = useCallback(() => {
    if (selectedIndex < 0 || lightboxImages.length === 0) return;
    const prevIndex = selectedIndex > 0 ? selectedIndex - 1 : lightboxImages.length - 1;
    setSelectedImageId(lightboxImages[prevIndex].id);
  }, [selectedIndex, lightboxImages]);

  const goToNext = useCallback(() => {
    if (selectedIndex < 0 || lightboxImages.length === 0) return;
    const nextIndex = selectedIndex < lightboxImages.length - 1 ? selectedIndex + 1 : 0;
    setSelectedImageId(lightboxImages[nextIndex].id);
  }, [selectedIndex, lightboxImages]);

  useEffect(() => {
    if (selectedImageId !== null && selectedIndex < 0) {
      setSelectedImageId(null);
    }
  }, [selectedImageId, selectedIndex]);

  // Keyboard navigation & body scroll lock
  useEffect(() => {
    if (selectedImageId === null) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedImageId, goToPrev, goToNext]);

  return (
    <section id="gallery" className="relative py-28 bg-zinc-950">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#50ced4]/20 to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[#50ced4] text-sm font-medium tracking-widest uppercase mb-4 block">Our Work</span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5 leading-tight">
            Event{' '}
            <span className="text-[#50ced4]">Gallery</span>
          </h2>
          <p className="text-zinc-400 text-lg leading-relaxed">
            See our equipment in action at real events. Quality sound and lighting setups that make every occasion special.
          </p>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-[#50ced4] text-zinc-950'
                  : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white border border-white/5'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Gallery grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((image) => (
            <div
              key={image.id}
              onClick={() => !failedImages.has(image.id) && openLightbox(image.id)}
              className="group relative aspect-square rounded-xl overflow-hidden bg-zinc-900/50 border border-white/5 hover:border-[#50ced4]/30 transition-all duration-500 cursor-pointer"
            >
              {failedImages.has(image.id) ? (
                /* Placeholder when image not found */
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-900/80">
                  <div className="w-16 h-16 rounded-2xl bg-[#50ced4]/10 flex items-center justify-center mb-3">
                    <Camera className="w-7 h-7 text-[#50ced4]/60" />
                  </div>
                  <p className="text-zinc-500 text-xs font-medium">{image.category}</p>
                  <p className="text-zinc-600 text-[10px] mt-1">Image coming soon</p>
                </div>
              ) : (
                <>
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    onError={() => handleImageError(image.id)}
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <span className="text-xs text-[#50ced4] font-medium">{image.category}</span>
                    <p className="text-white text-sm font-medium mt-1">{image.alt}</p>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedIndex >= 0 && lightboxImages[selectedIndex] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Previous button */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            className="absolute left-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Image */}
          <div
            className="max-w-5xl max-h-[85vh] px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImages[selectedIndex].src}
              alt={lightboxImages[selectedIndex].alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4">
              <span className="text-[#50ced4] text-sm font-medium">
                {lightboxImages[selectedIndex].category}
              </span>
              <p className="text-white text-base mt-1">
                {lightboxImages[selectedIndex].alt}
              </p>
              <p className="text-zinc-500 text-sm mt-1">
                {selectedIndex + 1} / {lightboxImages.length}
              </p>
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </section>
  );
};
